(() => {
  const DEBUG = true;
  const log = (...args) => DEBUG && console.log("[preprinted-fallback]", ...args);

  // If any legacy script checks for a flag, it should stop running
  window.__preprintedFallbackV2 = true;

  let submitLock = false;

  // Run after Dawn has finished its own UI updates
  const raf2 = (fn) => requestAnimationFrame(() => requestAnimationFrame(fn));

  // Per product-info debounce/throttle
  const AV_STATE = new WeakMap(); // productInfoEl -> { timer, running, lastRun }

  // Cart qty cache to avoid race right after add
  let CART_QTY_CACHE = null; // { [variantId]: qty }
  let CART_QTY_CACHE_AT = 0;

  // ---------------- utils ----------------

  function safeParseJsonScript(id, fallback) {
    const el = document.getElementById(id);
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent || "");
    } catch (e) {
      console.warn("[preprinted-fallback] JSON parse failed:", id, e);
      return fallback;
    }
  }

  function getSectionId(productInfoEl) {
    return (
      productInfoEl?.dataset?.section ||
      productInfoEl?.dataset?.sectionId ||
      productInfoEl?.getAttribute?.("data-section") ||
      productInfoEl?.getAttribute?.("data-section-id") ||
      null
    );
  }

  function getRequestedQty(productInfoEl) {
    const form = productInfoEl.querySelector('form[action^="/cart/add"]');
    const qtyInput =
      form?.querySelector('input[name="quantity"]') ||
      productInfoEl.querySelector('input[name="quantity"]');
    const n = qtyInput ? parseInt(qtyInput.value || "1", 10) : 1;
    return Number.isFinite(n) && n > 0 ? n : 1;
  }

  async function fetchCartJson() {
    const res = await fetch("/cart.js", {
      headers: { Accept: "application/json" },
      cache: "no-store",
    });
    if (!res.ok) throw new Error(`cart.js failed (${res.status})`);
    return res.json();
  }

  function cartToQtyMap(cart) {
    const map = {};
    for (const line of cart.items || []) {
      const id = String(line.variant_id);
      map[id] = (map[id] || 0) + (line.quantity || 0);
    }
    return map;
  }

  async function getCartQtyByVariantId() {
    const now = Date.now();
    if (CART_QTY_CACHE && now - CART_QTY_CACHE_AT < 800) return CART_QTY_CACHE;

    try {
      const cart = await fetchCartJson();
      CART_QTY_CACHE = cartToQtyMap(cart);
      CART_QTY_CACHE_AT = Date.now();
      return CART_QTY_CACHE;
    } catch (e) {
      console.warn("[preprinted-fallback] cart.js fetch failed", e);
      return CART_QTY_CACHE || {};
    }
  }

  function bumpCartQtyCache(variantId, qty) {
    if (!variantId || !qty) return;
    if (!CART_QTY_CACHE) CART_QTY_CACHE = {};
    const key = String(variantId);
    CART_QTY_CACHE[key] = (CART_QTY_CACHE[key] || 0) + Number(qty || 0);
    CART_QTY_CACHE_AT = Date.now();
  }

  function remainingPreprintedQty(preQtyByVariant, cartQtyByVariant, preVariantId) {
    const total = parseInt(preQtyByVariant[String(preVariantId)] || "0", 10) || 0;
    const inCart = parseInt(cartQtyByVariant[String(preVariantId)] || "0", 10) || 0;
    return Math.max(0, total - inCart);
  }

  async function addOne(variantId, quantity) {
    if (!variantId || quantity <= 0) return;

    const res = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      cache: "no-store",
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: Number(quantity) }],
      }),
    });

    if (!res.ok) {
      const txt = await res.text();
      throw new Error(`cart/add.js failed (${res.status}): ${txt}`);
    }

    // optimistic cache update to avoid “needs reload” state changes
    bumpCartQtyCache(variantId, quantity);

    return res.json();
  }

  function buildReverseMap(podToPre) {
    const preToPod = {};
    for (const [podId, preId] of Object.entries(podToPre || {})) {
      if (preId) preToPod[String(preId)] = String(podId);
    }
    return preToPod;
  }

  function findPodVariant(podVariants, podVariantId) {
    return (podVariants || []).find((v) => String(v.id) === String(podVariantId));
  }

  // ---------------- drawer patching (sections) ----------------

  function getRootUrl() {
    const root = window.Shopify?.routes?.root || "/";
    return root.startsWith("/") ? root : `/${root}`;
  }

  function uniq(arr) {
    return Array.from(new Set((arr || []).filter(Boolean)));
  }

  function sectionKey(entry) {
    return entry?.section || entry?.id || null;
  }

  function getSectionInnerHTML(html, selector) {
    const doc = new DOMParser().parseFromString(html, "text/html");
    const el = doc.querySelector(selector);
    return el ? el.innerHTML : "";
  }

  function getSectionsToRender() {
    const drawer = document.querySelector("cart-drawer");
    const note = document.querySelector("cart-notification");

    let list = null;
    try {
      list = drawer?.getSectionsToRender?.();
    } catch (_) {}
    if (!Array.isArray(list) || !list.length) {
      try {
        list = note?.getSectionsToRender?.();
      } catch (_) {}
    }

    if (!Array.isArray(list) || !list.length) {
      list = [
        { id: "cart-drawer", section: "cart-drawer", selector: ".drawer__inner" },
        { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: "#cart-icon-bubble" },
        { id: "cart-live-region-text", section: "cart-live-region-text", selector: "#cart-live-region-text" },
      ];
    }

    const ensure = [
      { id: "cart-icon-bubble", section: "cart-icon-bubble", selector: "#cart-icon-bubble" },
      { id: "cart-live-region-text", section: "cart-live-region-text", selector: "#cart-live-region-text" },
    ];

    const byKey = new Map();
    [...list, ...ensure].forEach((e) => {
      const k = `${sectionKey(e)}::${e.selector}`;
      if (!byKey.has(k)) byKey.set(k, e);
    });

    return Array.from(byKey.values());
  }

  async function fetchSections(sectionNames) {
    const idsParam = sectionNames.map(encodeURIComponent).join(",");
    const url = `${getRootUrl()}?sections=${idsParam}&_=${Date.now()}`;
    const res = await fetch(url, { headers: { Accept: "application/json" }, cache: "no-store" });
    if (!res.ok) {
      const txt = await res.text().catch(() => "");
      throw new Error(`sections fetch failed (${res.status}): ${txt}`);
    }
    return res.json();
  }

  function patchOne(entry, sections, drawer, note) {
    const key = sectionKey(entry);
    const html = key ? sections[key] : null;
    if (!html) return false;

    const selector = entry.selector;

    if (
      selector === "#cart-icon-bubble" ||
      selector === "#cart-live-region-text" ||
      selector === "#cart-notification"
    ) {
      const doc = new DOMParser().parseFromString(html, "text/html");
      const fresh = doc.querySelector(selector);
      const current = document.querySelector(selector);
      if (!fresh || !current) return false;
      current.outerHTML = fresh.outerHTML;
      return true;
    }

    const newInner = getSectionInnerHTML(html, selector);
    if (!newInner) return false;

    let target = null;
    if (key === "cart-drawer") target = drawer?.querySelector(selector);
    if (!target && key === "cart-notification") target = note?.querySelector(selector);
    if (!target) target = document.querySelector(selector);
    if (!target) return false;

    target.innerHTML = newInner;
    return true;
  }

  // ---------------- CART COUNT (robust) ----------------

  function updateCartCountEverywhere(cart) {
    const count = Number(cart?.item_count || 0);

    const numberSelectors = [
      "#cart-icon-bubble [aria-hidden='true']",
      "#cart-icon-bubble .cart-count-bubble span",
      ".cart-count-bubble [aria-hidden='true']",
      ".cart-count-bubble span",
      "[data-cart-count]",
      "[data-cart-count-bubble]",
      "[data-cart-count-badge]",
      ".cart-count",
      ".header__cart-count",
      ".header-cart-count",
      ".cart-bubble__count",
      ".cart-count-number",
    ];

    document.querySelectorAll(numberSelectors.join(",")).forEach((el) => {
      if (!el) return;
      if (
        el.children &&
        el.children.length > 0 &&
        !el.matches("[data-cart-count], .cart-count, .header__cart-count, .header-cart-count")
      )
        return;
      el.textContent = String(count);
    });

    const hiddenSelectors = [
      "#cart-icon-bubble .visually-hidden",
      ".cart-count-bubble .visually-hidden",
    ];
    document.querySelectorAll(hiddenSelectors.join(",")).forEach((el) => {
      if (!el) return;
      el.textContent = `${count} item${count === 1 ? "" : "s"}`;
    });

    const bubbleContainers = document.querySelectorAll(
      "#cart-icon-bubble, .cart-count-bubble, [data-cart-count-bubble], [data-cart-count-badge]"
    );
    bubbleContainers.forEach((el) => {
      if (!el) return;
      if (count > 0) {
        el.classList.remove("hidden");
        el.removeAttribute("hidden");
        if (el.style?.display === "none") el.style.display = "";
      } else {
        el.classList.add("hidden");
        el.setAttribute("hidden", "hidden");
      }
    });

    const iconBubble = document.querySelector("#cart-icon-bubble");
    if (iconBubble) {
      let bubble = iconBubble.querySelector(".cart-count-bubble");
      if (count > 0) {
        if (!bubble) {
          bubble = document.createElement("div");
          bubble.className = "cart-count-bubble";
          bubble.innerHTML = `
            <span aria-hidden="true">${count}</span>
            <span class="visually-hidden">${count} item${count === 1 ? "" : "s"}</span>
          `;
          iconBubble.appendChild(bubble);
        } else {
          const n = bubble.querySelector("[aria-hidden='true']") || bubble.querySelector("span");
          if (n) n.textContent = String(count);
          const vh = bubble.querySelector(".visually-hidden");
          if (vh) vh.textContent = `${count} item${count === 1 ? "" : "s"}`;
        }
      } else if (bubble) {
        bubble.remove();
      }
    }
  }

  function applyCartState(cart) {
    updateCartCountEverywhere(cart);

    const itemCount = Number(cart?.item_count || 0);
    const isEmpty = itemCount === 0;

    const drawer = document.querySelector("cart-drawer");
    const drawerRoot = document.querySelector("#CartDrawer");
    const note = document.querySelector("cart-notification");

    if (drawer) drawer.classList.toggle("is-empty", isEmpty);
    if (drawerRoot) drawerRoot.classList.toggle("is-empty", isEmpty);
    if (note) note.classList.toggle("is-empty", isEmpty);

    const extra = [
      "cart-drawer-items",
      ".drawer__inner",
      ".drawer__contents",
      ".drawer__footer",
      ".cart-drawer__footer",
    ];
    extra.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => el.classList.toggle("is-empty", isEmpty));
    });

    if (!isEmpty && drawer) {
      const unhide = [
        ".drawer__inner",
        ".drawer__contents",
        ".drawer__footer",
        ".cart-drawer__footer",
        ".drawer__header",
      ];
      unhide.forEach((sel) => {
        drawer.querySelectorAll(sel).forEach((el) => {
          if (el?.style?.display === "none") el.style.display = "";
        });
      });
    }
  }

  async function refreshDawnCartUI() {
    const drawer = document.querySelector("cart-drawer");
    const note = document.querySelector("cart-notification");

    const entries = getSectionsToRender();
    const names = uniq(entries.map((e) => sectionKey(e)));

    let cart;
    try {
      cart = await fetchCartJson();
      // keep qty cache in sync with real cart
      CART_QTY_CACHE = cartToQtyMap(cart);
      CART_QTY_CACHE_AT = Date.now();
    } catch (e) {
      console.warn("[preprinted-fallback] cart.js failed", e);
      cart = { item_count: 0, items: [] };
    }

    let sections;
    try {
      sections = await fetchSections(names);
    } catch (e) {
      console.warn("[preprinted-fallback] refreshDawnCartUI sections failed", e);
      applyCartState(cart);
      requestAnimationFrame(() => updateCartCountEverywhere(cart));
      return;
    }

    for (const entry of entries) patchOne(entry, sections, drawer, note);

    applyCartState(cart);
    requestAnimationFrame(() => updateCartCountEverywhere(cart));

    try {
      if (drawer && typeof drawer.open === "function") drawer.open();
      else if (note && typeof note.open === "function") note.open();
    } catch (_) {}

    document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
  }

  // ---------------- VARIANT UI OVERRIDES ----------------

  function getVariantPicker(productInfoEl) {
    return (
      productInfoEl.querySelector("variant-radios") ||
      productInfoEl.querySelector("variant-selects") ||
      null
    );
  }

  function normalize(v) {
    return String(v ?? "").trim();
  }

  function getRadioLabel(input, scope) {
    if (!input) return null;
    if (input.labels && input.labels.length) return input.labels[0];

    const id = input.getAttribute("id");
    if (id && scope) {
      try {
        const esc = window.CSS && CSS.escape ? CSS.escape(id) : id;
        const lbl = scope.querySelector(`label[for="${esc}"]`);
        if (lbl) return lbl;
      } catch (_) {}
    }
    const sib = input.nextElementSibling;
    if (sib && sib.tagName === "LABEL") return sib;
    return null;
  }

  function clearDisabledSignals(el) {
    if (!el) return;
    el.classList?.remove(
      "disabled",
      "is-disabled",
      "soldout",
      "sold-out",
      "unavailable",
      "is-unavailable"
    );
    el.removeAttribute?.("aria-disabled");
    el.removeAttribute?.("data-disabled");
    el.removeAttribute?.("data-unavailable");
    el.removeAttribute?.("disabled");
    el.removeAttribute?.("tabindex");

    if (el.style) {
      if (el.style.pointerEvents === "none") el.style.pointerEvents = "";
      if (el.style.opacity) el.style.opacity = "";
      if (el.style.filter) el.style.filter = "";
      if (el.style.display === "none") el.style.display = "";
    }
  }

  function hideSoldOutBits(root) {
    if (!root) return;
    const selectors = [
      ".price__badge-sold-out",
      ".badge--soldout",
      ".badge--sold-out",
      ".soldout",
      ".sold-out",
      "[data-badge='sold-out']",
      "[data-sold-out]",
      "[data-soldout]",
      ".sold-out-message",
      ".unavailable-message",
    ];
    root.querySelectorAll(selectors.join(",")).forEach((el) => {
      el.classList?.add("hidden");
      el.style.display = "none";
    });
  }

  function showSoldOutBits(root) {
    if (!root) return;
    const selectors = [
      ".price__badge-sold-out",
      ".badge--soldout",
      ".badge--sold-out",
      ".soldout",
      ".sold-out",
      "[data-badge='sold-out']",
      "[data-sold-out]",
      "[data-soldout]",
    ];
    root.querySelectorAll(selectors.join(",")).forEach((el) => {
      el.classList?.remove("hidden");
      if (el.style?.display === "none") el.style.display = "";
    });
  }

  function showSubmitText(productFormEl) {
    const submit = productFormEl?.querySelector?.('[type="submit"]');
    const span = productFormEl?.submitButtonText || submit?.querySelector?.("span");
    if (span) span.classList.remove("hidden");
    const sold = productFormEl?.querySelector?.(".sold-out-message");
    if (sold) sold.classList.add("hidden");
  }

  function forceAddToCartEnabled(productInfoEl, combinedAvail) {
    if (!combinedAvail) return;

    const productFormEl = productInfoEl.querySelector("product-form");
    const form = productFormEl?.querySelector("form") || productInfoEl.querySelector('form[action^="/cart/add"]');
    const submit =
      productFormEl?.querySelector?.('[type="submit"]') ||
      form?.querySelector?.('[type="submit"]') ||
      null;

    const addText = window.variantStrings?.addToCart || "Add to cart";

    if (productFormEl && typeof productFormEl.toggleSubmitButton === "function") {
      try {
        productFormEl.toggleSubmitButton(false);
      } catch (_) {}
    }

    showSubmitText(productFormEl);

    if (submit) {
      submit.disabled = false;
      submit.removeAttribute("disabled");
      submit.setAttribute("aria-disabled", "false");
      clearDisabledSignals(submit);

      const span = productFormEl?.submitButtonText || submit.querySelector("span") || null;
      if (span) {
        span.classList.remove("hidden");
        span.textContent = addText;
      } else {
        if ((submit.textContent || "").toLowerCase().includes("sold")) submit.textContent = addText;
      }

      let p = submit.parentElement;
      for (let i = 0; i < 4 && p; i++) {
        clearDisabledSignals(p);
        p = p.parentElement;
      }
    }

    const payBtn =
      productInfoEl.querySelector(".shopify-payment-button__button") ||
      productInfoEl.querySelector(".shopify-payment-button button") ||
      productInfoEl.querySelector("[data-shopify-payment-button] button");
    if (payBtn) {
      payBtn.disabled = false;
      payBtn.removeAttribute("disabled");
      payBtn.setAttribute("aria-disabled", "false");
      clearDisabledSignals(payBtn);
    }

    hideSoldOutBits(productInfoEl);
  }

  function forceSoldOutUI(productInfoEl) {
    const productFormEl = productInfoEl.querySelector("product-form");
    const form = productFormEl?.querySelector("form") || productInfoEl.querySelector('form[action^="/cart/add"]');
    const submit =
      productFormEl?.querySelector?.('[type="submit"]') ||
      form?.querySelector?.('[type="submit"]') ||
      null;

    const soldText = window.variantStrings?.soldOut || "Sold out";

    if (productFormEl && typeof productFormEl.toggleSubmitButton === "function") {
      try {
        productFormEl.toggleSubmitButton(true, soldText);
      } catch (_) {}
    }

    if (submit) {
      submit.disabled = true;
      submit.setAttribute("disabled", "disabled");
      submit.setAttribute("aria-disabled", "true");
      // Keep span visible with Sold out
      const span = productFormEl?.submitButtonText || submit.querySelector("span") || null;
      if (span) {
        span.classList.remove("hidden");
        span.textContent = soldText;
      } else {
        submit.textContent = soldText;
      }
    }

    // Disable Buy It Now / payment button
    const payBtn =
      productInfoEl.querySelector(".shopify-payment-button__button") ||
      productInfoEl.querySelector(".shopify-payment-button button") ||
      productInfoEl.querySelector("[data-shopify-payment-button] button");
    if (payBtn) {
      payBtn.disabled = true;
      payBtn.setAttribute("disabled", "disabled");
      payBtn.setAttribute("aria-disabled", "true");
    }

    // Restore sold-out badges if theme has them
    showSoldOutBits(productInfoEl);
  }

  function hardEnablePill(input, scope) {
    if (!input) return;

    input.disabled = false;
    input.removeAttribute("disabled");
    input.setAttribute("aria-disabled", "false");
    input.classList?.remove("disabled", "is-disabled", "soldout", "sold-out");
    input.removeAttribute("data-disabled");
    input.removeAttribute("data-unavailable");
    input.removeAttribute("tabindex");

    // mark that we overrode this (so we can undo later)
    input.dataset.preprintOverride = "1";

    const label = getRadioLabel(input, scope);
    if (label) {
      label.dataset.preprintOverride = "1";
      clearDisabledSignals(label);
      hideSoldOutBits(label);
    }

    // Clear a few ancestor levels (themes often disable wrappers)
    let p = label || input;
    for (let i = 0; i < 4 && p; i++) {
      p = p.parentElement;
      clearDisabledSignals(p);
    }
  }

  function hardDisablePillIfWeOverrode(input, scope) {
    if (!input) return;
    if (input.dataset.preprintOverride !== "1") return;

    input.disabled = true;
    input.setAttribute("disabled", "disabled");
    input.setAttribute("aria-disabled", "true");
    input.classList?.add("disabled");

    const label = getRadioLabel(input, scope);
    if (label) {
      label.classList?.add("disabled");
      label.setAttribute?.("aria-disabled", "true");
      // allow sold out badges to show again
      showSoldOutBits(label);
      delete label.dataset.preprintOverride;
    }

    delete input.dataset.preprintOverride;
  }

  function updateVariantPickerAvailability(productInfoEl, podVariants, combinedByPodVariantId) {
    const picker = getVariantPicker(productInfoEl);
    if (!picker || !Array.isArray(podVariants) || !podVariants.length) return;

    const variantIdSet = new Set(podVariants.map((v) => normalize(v.id)));

    // Current selection values
    const selectedOptions = [];
    const fieldsets = picker.querySelectorAll("fieldset");

    if (fieldsets.length) {
      fieldsets.forEach((fs) => {
        const checked = fs.querySelector('input[type="radio"]:checked');
        selectedOptions.push(checked ? checked.value : null);
      });
    } else {
      const selects = picker.querySelectorAll("select");
      selects.forEach((s) => selectedOptions.push(s.value || null));
    }

    const optionCount = podVariants[0]?.options?.length || 0;

    const variants = podVariants.map((v) => ({
      id: normalize(v.id),
      options: v.options || [],
      available: !!combinedByPodVariantId[normalize(v.id)],
    }));

    // RADIO/PILLS
    if (fieldsets.length) {
      fieldsets.forEach((fs, optionIndex) => {
        const inputs = fs.querySelectorAll('input[type="radio"]');
        if (!inputs.length) return;

        const isVariantIdMode = Array.from(inputs).some((inp) => variantIdSet.has(normalize(inp.value)));

        inputs.forEach((input) => {
          const val = input.value;
          let ok = false;

          if (isVariantIdMode) {
            ok = !!combinedByPodVariantId[normalize(val)];
          } else if (optionCount) {
            const candidateValue = val;
            ok = variants.some((v) => {
              if (!v.available) return false;
              if (v.options[optionIndex] !== candidateValue) return false;
              for (let j = 0; j < optionCount; j++) {
                if (j === optionIndex) continue;
                if (selectedOptions[j] && v.options[j] !== selectedOptions[j]) return false;
              }
              return true;
            });
          }

          if (ok) hardEnablePill(input, picker);
          else hardDisablePillIfWeOverrode(input, picker);
        });
      });

      return;
    }

    // SELECT dropdowns (only undo our own overrides if needed is complex; keep it simple)
    const selects = picker.querySelectorAll("select");
    if (!selects.length) return;

    selects.forEach((select, optionIndex) => {
      const opts = Array.from(select.options || []);
      const isVariantIdMode = opts.some((o) => variantIdSet.has(normalize(o.value)));

      opts.forEach((opt) => {
        const val = opt.value;
        let ok = false;

        if (isVariantIdMode) {
          ok = !!combinedByPodVariantId[normalize(val)];
        } else if (optionCount) {
          const candidateValue = val;
          ok = variants.some((v) => {
            if (!v.available) return false;
            if (v.options[optionIndex] !== candidateValue) return false;
            for (let j = 0; j < optionCount; j++) {
              if (j === optionIndex) continue;
              if (selectedOptions[j] && v.options[j] !== selectedOptions[j]) return false;
            }
            return true;
          });
        }

        if (ok) opt.disabled = false;
      });
    });
  }

  // ---------------- availability refresh ----------------

  async function refreshAvailability(productInfoEl) {
    const sectionId = getSectionId(productInfoEl);
    if (!sectionId) return;

    const podToPre = safeParseJsonScript(`PodToPreprintedVariant-${sectionId}`, null);
    if (!podToPre) return;

    const preQtyByVariant = safeParseJsonScript(`PreprintedQtyByVariant-${sectionId}`, {});
    const podVariants = safeParseJsonScript(`PodVariantData-${sectionId}`, []);

    const form = productInfoEl.querySelector('form[action^="/cart/add"]');
    const idInput = form?.querySelector('input[name="id"]');
    if (!idInput) return;

    const rawId = String(idInput.value || "");
    if (!rawId) return;

    const preToPod = buildReverseMap(podToPre);
    const podVariantId = podToPre[rawId] ? rawId : (preToPod[rawId] || rawId);
    const mappedPreId = podToPre[podVariantId];

    const cartQtyByVariant = await getCartQtyByVariantId();

    // Build combined availability for ALL pod variants
    const combinedByPodVariantId = {};
    for (const v of podVariants || []) {
      const podId = String(v.id);
      const preId = podToPre[podId];
      const remPreAll = preId ? remainingPreprintedQty(preQtyByVariant, cartQtyByVariant, preId) : 0;
      combinedByPodVariantId[podId] = !!v.available || remPreAll > 0;
    }

    const remPre = mappedPreId
      ? remainingPreprintedQty(preQtyByVariant, cartQtyByVariant, mappedPreId)
      : 0;

    const podIsAvailable = !!findPodVariant(podVariants, podVariantId)?.available;
    const combinedAvail = podIsAvailable || remPre > 0;

    // ✅ enable OR revert to sold out immediately
    if (combinedAvail) {
      forceAddToCartEnabled(productInfoEl, true);
    } else {
      // POD sold out + no remaining preprint => revert UI to sold out
      forceSoldOutUI(productInfoEl);
    }

    // ✅ enable/disable pills that we override
    updateVariantPickerAvailability(productInfoEl, podVariants, combinedByPodVariantId);

    log("refresh availability", { sectionId, podVariantId, mappedPreId, remPre, podIsAvailable, combinedAvail });
  }

  function scheduleRefreshAvailability(productInfoEl) {
    let st = AV_STATE.get(productInfoEl);
    if (!st) {
      st = { timer: null, running: false, lastRun: 0 };
      AV_STATE.set(productInfoEl, st);
    }

    if (st.timer) clearTimeout(st.timer);
    st.timer = setTimeout(async () => {
      if (st.running) return;
      const now = Date.now();
      if (now - st.lastRun < 80) return;

      st.running = true;
      st.lastRun = now;
      try {
        await refreshAvailability(productInfoEl);
      } finally {
        st.running = false;
      }
    }, 50);
  }

  function initAll() {
    document.querySelectorAll("product-info").forEach((pi) => {
      if (pi.dataset.preprintedSwapInit === "1") return;
      pi.dataset.preprintedSwapInit = "1";

      const sectionId = getSectionId(pi);
      if (!sectionId) return;
      if (!document.getElementById(`PodToPreprintedVariant-${sectionId}`)) return;

      // Run after Dawn’s change handling
      pi.addEventListener("change", () => raf2(() => scheduleRefreshAvailability(pi)), true);

      ["variantChange", "variant:change", "variant-change"].forEach((evtName) => {
        pi.addEventListener(evtName, () => raf2(() => scheduleRefreshAvailability(pi)), true);
      });

      raf2(() => scheduleRefreshAvailability(pi));
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }

  // when cart refreshes (drawer updates), re-evaluate availability so sold-out can flip immediately
  document.addEventListener("cart:refresh", () => {
    document.querySelectorAll("product-info").forEach((pi) => {
      if (pi.dataset.preprintedSwapInit === "1") raf2(() => scheduleRefreshAvailability(pi));
    });
  });

  // ---------------- submit handling (single source of truth) ----------------

  document.addEventListener(
    "submit",
    async (e) => {
      const form = e.target;
      if (!(form instanceof HTMLFormElement)) return;

      const action = (form.getAttribute("action") || "").toLowerCase();
      if (!action.includes("/cart/add")) return;

      const productInfoEl = form.closest("product-info");
      if (!productInfoEl) return;

      const sectionId = getSectionId(productInfoEl);
      if (!sectionId) return;

      const podToPre = safeParseJsonScript(`PodToPreprintedVariant-${sectionId}`, null);
      if (!podToPre) return;

      const preQtyByVariant = safeParseJsonScript(`PreprintedQtyByVariant-${sectionId}`, {});
      const podVariants = safeParseJsonScript(`PodVariantData-${sectionId}`, []);

      const idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;

      const rawId = String(idInput.value || "");
      if (!rawId) return;

      const preToPod = buildReverseMap(podToPre);
      const podVariantId = podToPre[rawId] ? rawId : (preToPod[rawId] || rawId);
      const mappedPreId = podToPre[podVariantId];

      if (!mappedPreId) return;

      if (submitLock) {
        e.preventDefault();
        e.stopImmediatePropagation();
        return;
      }
      submitLock = true;

      e.preventDefault();
      e.stopImmediatePropagation();

      // Keep the form on POD id; we do the split adds ourselves
      idInput.value = String(podVariantId);

      const requestedQty = getRequestedQty(productInfoEl);
      const cartQtyByVariant = await getCartQtyByVariantId();

      const remPre = remainingPreprintedQty(preQtyByVariant, cartQtyByVariant, mappedPreId);
      const podIsAvailable = !!findPodVariant(podVariants, podVariantId)?.available;

      const qtyFromPre = Math.min(requestedQty, remPre);
      const qtyFromPodPlanned = requestedQty - qtyFromPre;

      log("submit decide", {
        podVariantId,
        mappedPreId,
        requestedQty,
        remPre,
        qtyFromPre,
        qtyFromPodPlanned,
        podIsAvailable,
      });

      let addedSomething = false;

      try {
        let qtyFromPod = qtyFromPodPlanned;

        // 1) Try preprinted first
        if (qtyFromPre > 0) {
          try {
            await addOne(mappedPreId, qtyFromPre);
            addedSomething = true;
            log("added preprinted", { mappedPreId, qtyFromPre });
          } catch (err) {
            console.warn("[preprinted-fallback] preprinted add failed; fallback to POD", err);
            qtyFromPod = requestedQty;
          }
        }

        // 2) POD remainder
        if (qtyFromPod > 0) {
          if (!podIsAvailable) {
            if (addedSomething) alert(`Only ${qtyFromPre} item(s) were available and added.`);
            else alert("This variant is currently sold out.");

            // ✅ immediately flip UI to sold out (no reload)
            raf2(() => scheduleRefreshAvailability(productInfoEl));
          } else {
            await addOne(podVariantId, qtyFromPod);
            addedSomething = true;
            log("added POD", { podVariantId, qtyFromPod });
          }
        }

        if (addedSomething) {
          await refreshDawnCartUI();
          // ✅ after cart update, re-check (preprint might now be exhausted)
          raf2(() => scheduleRefreshAvailability(productInfoEl));
        }
      } catch (err) {
        console.error(err);
        alert("Could not add to cart. Check console for details.");
      } finally {
        submitLock = false;
      }
    },
    true
  );
})();
